// src/components/foods/FoodDealers.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import "./foods.css";
import { fetchKhPartyRows, saveKhParty } from "../../services/foods";

/* ---------------- helpers ---------------- */
function safeParseArray(v) {
  try {
    const s = typeof v === "string" ? v.trim() : v;
    const out = typeof s === "string" ? JSON.parse(s) : s;
    return Array.isArray(out) ? out : [];
  } catch {
    return [];
  }
}

function statusCategory(s) {
  const x = String(s || "").trim().toLowerCase();
  if (x.includes("on") || x.includes("active")) return 1;
  if (x.includes("off") || x.includes("inactive") || x.includes("deactiv") || x.includes("close")) return -1;
  return 0;
}

// short but effective (same idea as Android)
function devanagariToLatin(s) {
  if (!s) return "";
  let out = "";
  for (const ch of String(s)) {
    switch (ch) {
      case "अ": out += "a"; break;  case "आ": out += "aa"; break;
      case "इ": out += "i"; break;  case "ई": out += "ii"; break;
      case "उ": out += "u"; break;  case "ऊ": out += "uu"; break;
      case "ए": out += "e"; break;  case "ऐ": out += "ai"; break;
      case "ओ": out += "o"; break;  case "औ": out += "au"; break;
      case "क": out += "k"; break;  case "ख": out += "kh"; break;
      case "ग": out += "g"; break;  case "घ": out += "gh"; break;
      case "च": out += "ch"; break; case "छ": out += "chh"; break;
      case "ज": out += "j"; break;  case "झ": out += "jh"; break;
      case "ट": out += "t"; break;  case "ठ": out += "th"; break;
      case "ड": out += "d"; break;  case "ढ": out += "dh"; break;
      case "त": out += "t"; break;  case "थ": out += "th"; break;
      case "द": out += "d"; break;  case "ध": out += "dh"; break;
      case "न": out += "n"; break;  case "प": out += "p"; break;
      case "फ": out += "ph"; break; case "ब": out += "b"; break;
      case "भ": out += "bh"; break; case "म": out += "m"; break;
      case "य": out += "y"; break;  case "र": out += "r"; break;
      case "ल": out += "l"; break;  case "व": out += "v"; break;
      case "श": out += "sh"; break; case "ष": out += "sh"; break;
      case "स": out += "s"; break;  case "ह": out += "h"; break;
      case "ा": out += "aa"; break; case "ि": out += "i"; break;
      case "ी": out += "ii"; break; case "ु": out += "u"; break;
      case "ू": out += "uu"; break; case "े": out += "e"; break;
      case "ै": out += "ai"; break; case "ो": out += "o"; break;
      case "ौ": out += "au"; break;
      case "ं":
      case "ँ": out += "n"; break;
      case "्": break;
      default: out += ch;
    }
  }
  return out;
}

/* ---------------- Portal Modal ---------------- */
function PortalModal({ children }) {
  return ReactDOM.createPortal(children, document.body);
}

/* ---------------- Add/Edit Dealer Modal ---------------- */
function AddDealerModal({ open, onClose, onSaved, mainuser, editRow }) {
  const codeRef = useRef(null);

  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [address, setAddress] = useState("");

  // shown in screenshot (may be ignored by backend; safe to send)
  const [village, setVillage] = useState("");
  const [taluka, setTaluka] = useState("");
  const [district, setDistrict] = useState("");

  // we map this to API "obal" (Android uses OBAL)
  const [details, setDetails] = useState("");

  const [status, setStatus] = useState("Active");

  const [saveContinue, setSaveContinue] = useState(false);
  const [saving, setSaving] = useState(false);

  // fill edit/new
  useEffect(() => {
    if (!open) return;

    if (editRow) {
      setCode(String(editRow.code ?? ""));
      setName(String(editRow.name ?? ""));
      setMobile(String(editRow.mobile ?? ""));
      setAddress(String(editRow.address ?? ""));
      setVillage(String(editRow.village ?? ""));
      setTaluka(String(editRow.taluka ?? ""));
      setDistrict(String(editRow.district ?? ""));
      setDetails(String(editRow.details ?? editRow.obal ?? ""));
      setStatus(String(editRow.status ?? "Active") || "Active");
      setSaveContinue(false);
    } else {
      setCode("");
      setName("");
      setMobile("");
      setAddress("");
      setVillage("");
      setTaluka("");
      setDistrict("");
      setDetails("");
      setStatus("Active");
      setSaveContinue(false);
      setTimeout(() => codeRef.current?.focus?.(), 0);
    }
  }, [open, editRow]);

  function validate() {
    if (!String(code).trim()) return "Code required";
    if (!String(name).trim()) return "Name required";
    if (!String(address).trim()) return "Address required";
    if (!String(village).trim()) return "Village required";
    if (!String(taluka).trim()) return "Taluka required";
    if (!String(district).trim()) return "District required";
    if (!String(status).trim() || status === "Select Status") return "Status required";
    return "";
  }

  async function onSave() {
    const err = validate();
    if (err) {
      alert(err);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        knoedit: editRow ? String(editRow.code ?? "") : "", // android sends knoedit
        sno: String(code || ""),                            // dealer code
        name: String(name || ""),
        mobno: String(mobile || ""),
        address: String(address || ""),
        obal: String(details || "0"),                       // ✅ map Details -> OBAL
        mainuser: String(mainuser || ""),
        flag: editRow ? "edit" : "new",
        status: String(status || "Active"),

        // ✅ extra safe fields (ignored if PHP doesn't use)
        village: String(village || ""),
        taluka: String(taluka || ""),
        district: String(district || ""),
        details: String(details || ""),
      };

      const resText = await saveKhParty(payload);
      const msg = String(resText || "").trim();

      if (msg.includes("Entry with the same values already exists")) {
        alert("Entry with the same values already exists.");
        codeRef.current?.focus?.();
        codeRef.current?.select?.();
        return;
      }

      // success
      await onSaved?.();

      if (saveContinue) {
        // reset (keep nothing)
        setCode("");
        setName("");
        setMobile("");
        setAddress("");
        setVillage("");
        setTaluka("");
        setDistrict("");
        setDetails("");
        setStatus("Active");
        setTimeout(() => codeRef.current?.focus?.(), 0);
      } else {
        onClose();
      }
    } catch (e) {
      alert(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  if (!open) return null;

  const isEdit = !!editRow;

  return (
    <PortalModal>
      <div className="fdBackdrop" onMouseDown={onClose}>
        <div className="fdModal" onMouseDown={(e) => e.stopPropagation()} style={{ maxWidth: 760 }}>
          <div className="fdModalHead">
            <div className="fdModalHeadTitle">{isEdit ? "Edit Dealer" : "Add New Dealer"}</div>
            <button type="button" className="fdCloseBtn" onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>

          <div className="fdModalBody">
            <div className="fdFormGrid">
              <div className="fdLabel">
                Code : <span className="fdReq">*</span>
              </div>
              <div>
                <input
                  ref={codeRef}
                  className="fdInput"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="Code"
                  disabled={isEdit}   // ✅ lock in edit mode (like Android)
                  style={isEdit ? { opacity: 0.7, cursor: "not-allowed" } : undefined}
                />
              </div>

              <div className="fdLabel">
                Name : <span className="fdReq">*</span>
              </div>
              <div>
                <input className="fdInput" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" />
              </div>

              <div className="fdLabel">Mobile :</div>
              <div>
                <input className="fdInput" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="Mobile" />
              </div>

              <div className="fdLabel">
                Address : <span className="fdReq">*</span>
              </div>
              <div>
                <input className="fdInput" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Address" />
              </div>

              <div className="fdLabel">
                Village : <span className="fdReq">*</span>
              </div>
              <div>
                <input className="fdInput" value={village} onChange={(e) => setVillage(e.target.value)} placeholder="Village" />
              </div>

              <div className="fdLabel">
                Taluka : <span className="fdReq">*</span>
              </div>
              <div>
                <input className="fdInput" value={taluka} onChange={(e) => setTaluka(e.target.value)} placeholder="Taluka" />
              </div>

              <div className="fdLabel">
                District : <span className="fdReq">*</span>
              </div>
              <div>
                <input className="fdInput" value={district} onChange={(e) => setDistrict(e.target.value)} placeholder="District" />
              </div>

              <div className="fdLabel">Details :</div>
              <div>
                <input className="fdInput" value={details} onChange={(e) => setDetails(e.target.value)} placeholder="Details" />
              </div>

              <div className="fdLabel">
                Status : <span className="fdReq">*</span>
              </div>
              <div>
                <select className="fdInput" value={status} onChange={(e) => setStatus(e.target.value)} style={{ maxWidth: 220 }}>
                  <option value="Select Status">Select Status</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>

              <div />
              <div className="fdCheckboxRow">
                <label>
                  <input
                    type="checkbox"
                    checked={saveContinue}
                    onChange={(e) => setSaveContinue(e.target.checked)}
                    disabled={saving}
                  />{" "}
                  Save and continue
                </label>
              </div>
            </div>

            <div className="fdActions">
              <button
                type="button"
                className="fdBtnGhost"
                onClick={() => {
                  setCode(isEdit ? code : "");
                  setName("");
                  setMobile("");
                  setAddress("");
                  setVillage("");
                  setTaluka("");
                  setDistrict("");
                  setDetails("");
                  setStatus("Active");
                }}
                disabled={saving}
              >
                Reset
              </button>

              <button type="button" className="fdBtnPrimary" onClick={onSave} disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </PortalModal>
  );
}

/* ---------------- Main Dealers screen ---------------- */
export default function FoodDealers({ mainuser, onBack }) {
  const [loading, setLoading] = useState(false);
  const [master, setMaster] = useState([]);
  const [search, setSearch] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);

  const load = useCallback(async () => {
    if (!mainuser) return;
    setLoading(true);
    try {
      const data = await fetchKhPartyRows({ mainuser });
      const arr = safeParseArray(data);

      const rows = arr.map((o, idx) => ({
        srno: idx + 1,
        sr: o?.sr ?? "",
        code: o?.kno ?? o?.code ?? "",

        name: o?.name ?? "",
        mobile: o?.mobno ?? o?.mobile ?? "",
        address: o?.address ?? "",

        village: o?.village ?? o?.gaon ?? "",
        taluka: o?.taluka ?? "",
        district: o?.district ?? "",

        // list has "Details" column; many backends use obal for it
        details: o?.details ?? o?.obal ?? "",
        obal: o?.obal ?? "",

        status: o?.status ?? "",
        _raw: o,
      }));

      setMaster(rows);
    } finally {
      setLoading(false);
    }
  }, [mainuser]);

  useEffect(() => {
    load();
  }, [load]);

  const counters = useMemo(() => {
    let active = 0, inactive = 0;
    for (const r of master) {
      const c = statusCategory(r.status);
      if (c === 1) active++;
      else if (c === -1) inactive++;
    }
    return { active, inactive, total: master.length };
  }, [master]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return master;

    return master.filter((r) => {
      const kno = String(r.code || "");
      const name = String(r.name || "");
      const latin = devanagariToLatin(name);
      const hay = `${kno} ${name} ${latin} ${r.mobile || ""} ${r.address || ""} ${r.village || ""} ${r.taluka || ""} ${r.district || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [master, search]);

  return (
    <div className="foodsPage">
      <div className="foodsTop">
        <div className="foodsTitle" style={{ gap: 10 }}>
          <button className="foodsLinkBtn" type="button" onClick={onBack} title="Back">
            ←
          </button>
          <span>Food Dealers</span>
        </div>

        <div className="foodsActions">
          <button
            className="foodsLinkBtn"
            type="button"
            onClick={() => {
              setEditRow(null);
              setModalOpen(true);
            }}
          >
            ＋ Add New Dealer
          </button>
        </div>
      </div>

      <div className="foodsCard">
        <div className="foodsCardHead" style={{ justifyContent: "space-between" }}>
          <div className="foodsCaption">Food Dealers</div>

          <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
            <div style={{ fontWeight: 600 }}>
              Active : <span style={{ color: "#0a7" }}>{counters.active}</span>{" "}
              Inactive : <span style={{ color: "#c33" }}>{counters.inactive}</span>{" "}
              Total : <span style={{ color: "#35a" }}>{counters.total}</span>
            </div>

            <div className="foodsSearchBox">
              <span className="foodsSearchLabel">Search</span>
              <input className="foodsInput" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search" />
            </div>
          </div>
        </div>

        <div className="foodsTableWrap">
          <table className="foodsTable">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Mobile</th>
                <th>Address</th>
                <th>Village</th>
                <th>Taluka</th>
                <th>District</th>
                <th>Details</th>
                <th>Status</th>
                <th style={{ width: 90 }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="foodsEmpty">Loading...</td></tr>
              ) : visible.length === 0 ? (
                <tr><td colSpan={10} className="foodsEmpty">No dealers</td></tr>
              ) : (
                <>
                  {visible.map((r) => (
                    <tr key={`${r.code}-${r.srno}`}>
                      <td>{r.code || "-"}</td>
                      <td><b>{r.name || "-"}</b></td>
                      <td>{r.mobile || "-"}</td>
                      <td>{r.address || "-"}</td>
                      <td>{r.village || "-"}</td>
                      <td>{r.taluka || "-"}</td>
                      <td>{r.district || "-"}</td>
                      <td>{r.details || "-"}</td>
                      <td>{r.status || "-"}</td>
                      <td>
                        {/* ✅ Edit -> opens modal with code locked */}
                        <button
                          className="foodsLinkBtn"
                          type="button"
                          title="Edit"
                          onClick={() => {
                            setEditRow(r);
                            setModalOpen(true);
                          }}
                        >
                          ✎
                        </button>

                        {/* Delete (not implemented here) */}
                        <button className="foodsLinkBtn" type="button" title="Delete" style={{ color: "red" }}>
                          🗑
                        </button>
                      </td>
                    </tr>
                  ))}

                  <tr>
                    <td colSpan={10} style={{ textAlign: "center", padding: 12, color: "#666" }}>
                      Showing {visible.length ? 1 : 0} to {visible.length} of {visible.length} entries
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AddDealerModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={load}
        mainuser={mainuser}
        editRow={editRow}
      />
    </div>
  );
}
